/**
 * PageHero - Static hero banner for section pages
 * Displays a single image with gradient overlay and title
 */

const PageHero = ({ imageUrl, title, subtitle, altText, imagePosition = 'center' }) => {
  return (
    <div 
      className="relative w-full h-48 sm:h-56 md:h-64 lg:h-72 overflow-hidden -m-4 lg:-m-6 mb-4 z-0 print:hidden"
      data-testid="page-hero"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          alt={altText || title}
          className="w-full h-full object-cover"
          style={{ objectPosition: imagePosition }}
        />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#2F6DB5]/70 via-[#2F6DB5]/50 to-transparent" />
      
      {/* Bottom fade for smooth transition to content */}
      <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />

      {/* Title Text */}
      <div className="absolute inset-0 flex items-center">
        <div className="px-6 sm:px-10 md:px-16 max-w-2xl">
          <h1 
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 drop-shadow-lg"
            style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p 
              className="text-sm sm:text-base text-white/90 drop-shadow-md"
              style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Hero images for each section
export const HERO_IMAGES = {
  compliance: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/42aa905e389ae538967a5d0f92f71ca75d9ad33ebecd235444318cacd82bbaea.png",
  settings: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/ffa72400fb3d5b768a756ecc73c60ebaebbd0f2866e70ad60b3351a126c3e30a.png",
  billing: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/8b7d3e2d281d7edff0d15efe0be7fff3801e80512297e33e469ca45edd86cefe.png",
  cacfp: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/a41c5ee331051215cb56549452ee8f4257078d02f5e2c74f4cd3e310d113c365.png",
  pickupSafety: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/7c3028da5a8baedd1b1193b87ce67289e7731322d2b5299aa4967ba28712f058.png",
  backup: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/853e69d7e5ef890eca0605abf0ae12a47421d519ab59fabb904b1ad31e1aaf85.png",
  customerPortal: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/d5176f05661da3f3c1a6174be73059c733d75f0f668651ba5a680a4ef887602f.png",
  analytics: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/73176300e932316f710027ab7be64b662a10ffe210718398d83493e2dbf9a2e3.png",
  manageChildren: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/b3cd19806032a47f36eb13ef88db14ab4d64f59078f8f97436c55f13730899c0.png",
  bulkUpload: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/1e5136660d2f868aca4d5fcfd6029b2e29009a80d6c0a5fb16e5ee5c18f99edb.png",
  childRecords: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/78ef8da7a97cf54b6a632ea142f8c89f3fa8e897fe1cfae96bf6cd150991eaf2.png",
  attendanceReport: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/b8cff573c7c598ff3dd8989e225e6a84a94c736e7ac223f8976a2314b9f97d33.png",
  dailyReports: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/dfd59ceeceacf388623a8cf899d71d4123a57baff90cee555e98a6e3ec0c0511.png",
  emergencyContact: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/b7320055f35a243137e3061906d5acdce1cfdcb5715f89388aafa4eff74d1a5a.png",
  fieldTripTags: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/6a5d2e534a3ca7e59b27a63f5f429bf323052408db715cc1aa84ee8dbc54ce85.png",
  staffTraining: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/24e43a86e70f44f45160f6e856f173f075feccd34e29aaa81136367360bbb617.png",
  manageStaff: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/7350d25bbfccac46476e1dc50ed6af84d1e16c675f4ca828ce566824f17a8ac1.png",
  staffAssignment: "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/4de968a42430bd69dc589bee17faf98df590b95e6eaa6c5a8d6191b91434d685.png"
};

export default PageHero;
